<template>
  <div v-if="view" class="decision-memory-review-card">
    <!-- 状态摘要 -->
    <div class="memory-header" :class="statusClass">
      <div class="memory-status-row">
        <span class="memory-badge" :class="statusClass">{{ statusBadgeText }}</span>
        <span class="memory-mode-label">{{ view.displayTitle }}</span>
      </div>
      <p class="memory-summary">{{ view.displaySummary }}</p>
      <div v-if="view.reuseMode === 'DIRECT_TO_DRAFT'" class="memory-need-review-tag">仍需人工确认</div>
    </div>

    <!-- 元信息 -->
    <div class="memory-meta">
      <span v-if="view.matchedPatternId" class="meta-chip">模式: {{ view.matchedPatternId }}</span>
      <span v-if="view.matchLevel" class="meta-chip" :class="view.matchLevel.toLowerCase()">{{ view.matchLevel }}</span>
      <span v-if="view.confidence" class="meta-chip">置信度: {{ formatConfidence(view.confidence) }}</span>
      <span v-if="view.requiresManualReview" class="meta-chip warn">需人工确认</span>
    </div>

    <!-- 审计说明 -->
    <div v-if="view.auditExplanation" class="memory-section">
      <div class="audit-explanation">{{ view.auditExplanation }}</div>
    </div>

    <!-- 审核提示与风险 -->
    <div v-if="view.reviewHints && view.reviewHints.length" class="memory-section">
      <div v-for="hint in view.reviewHints" :key="hint" class="hint-item">{{ hintLabel(hint) }}</div>
    </div>

    <div v-if="view.riskFlags && view.riskFlags.length" class="memory-section">
      <span v-for="flag in view.riskFlags" :key="flag" class="risk-tag">{{ flag }}</span>
    </div>

    <div v-if="view.degradeReasons && view.degradeReasons.length" class="memory-section">
      <details>
        <summary>降级原因 ({{ view.degradeReasons.length }})</summary>
        <ul class="reason-list">
          <li v-for="r in view.degradeReasons" :key="r">{{ r }}</li>
        </ul>
      </details>
    </div>

    <!-- 当前事件快照 -->
    <div v-if="view.currentEventSnapshot" class="memory-section">
      <details>
        <summary>当前事件快照</summary>
        <div class="snapshot-grid">
          <div class="snapshot-field"><label>期间</label><span>{{ view.currentEventSnapshot.period || '-' }}</span></div>
          <div class="snapshot-field"><label>金额</label><span>{{ view.currentEventSnapshot.amount || '-' }}</span></div>
          <div class="snapshot-field"><label>币种</label><span>{{ view.currentEventSnapshot.currency || '-' }}</span></div>
          <div class="snapshot-field"><label>交易类型</label><span>{{ view.currentEventSnapshot.normalizedTxnType || '-' }}</span></div>
          <div class="snapshot-field"><label>方向</label><span>{{ view.currentEventSnapshot.direction || '-' }}</span></div>
          <div class="snapshot-field"><label>来源记录</label><span class="mono">{{ view.currentEventSnapshot.sourceRecordId || '-' }}</span></div>
          <div class="snapshot-field"><label>来源系统</label><span>{{ view.currentEventSnapshot.sourceSystem || '-' }}</span></div>
          <div class="snapshot-field"><label>traceId</label><span class="mono">{{ view.currentEventSnapshot.traceId || '-' }}</span></div>
        </div>
      </details>
    </div>

    <!-- 历史分录复用 -->
    <div v-if="view.reusedLinePatterns && view.reusedLinePatterns.length" class="memory-section">
      <details open>
        <summary>历史分录复用 ({{ view.reusedLinePatterns.length }})</summary>
        <table class="pattern-table">
          <thead><tr><th>#</th><th>方向</th><th>科目模板</th><th>金额绑定</th><th>语义标签</th></tr></thead>
          <tbody>
            <tr v-for="lp in view.reusedLinePatterns" :key="lp.entryNo">
              <td>{{ lp.entryNo }}</td><td>{{ lp.side }}</td>
              <td class="mono">{{ lp.subjectTemplate }}</td><td>{{ lp.amountBinding }}</td>
              <td>{{ lp.lineSemanticTag || '-' }}</td>
            </tr>
          </tbody>
        </table>
      </details>
    </div>

    <!-- 候选提示 -->
    <div v-if="view.candidateHints && view.candidateHints.length" class="memory-section">
      <details>
        <summary>历史候选建议 ({{ view.candidateHints.length }})</summary>
        <div v-for="(hint, i) in view.candidateHints" :key="i" class="hint-card">
          <div class="hint-row"><label>模式:</label><span>{{ hint.matchedPatternId }}</span></div>
          <div class="hint-row"><label>说明:</label><span>{{ hint.hintMessage }}</span></div>
        </div>
      </details>
    </div>

    <!-- matchedSignals / changedSignals -->
    <div class="memory-section signal-section">
      <details>
        <summary>匹配信号 ({{ view.matchedSignals ? view.matchedSignals.length : 0 }}) / 差异 ({{ view.changedSignals ? view.changedSignals.length : 0 }})</summary>
        <div v-if="view.matchedSignals && view.matchedSignals.length" class="signal-group">
          <div class="signal-group-label">匹配信号</div>
          <ul class="signal-list matched"><li v-for="s in view.matchedSignals" :key="s">{{ s }}</li></ul>
        </div>
        <div v-if="view.changedSignals && view.changedSignals.length" class="signal-group">
          <div class="signal-group-label">差异信号</div>
          <ul class="signal-list changed"><li v-for="s in view.changedSignals" :key="s">{{ s }}</li></ul>
        </div>
      </details>
    </div>

    <!-- 操作按钮 -->
    <div v-if="view.availableActions && view.availableActions.length" class="memory-actions">
      <button v-for="act in view.availableActions"
              :key="act.actionCode"
              class="action-btn"
              :class="btnClass(act.actionCode)"
              @click="handleActionClick(act)">
        {{ act.actionLabel }}
      </button>
    </div>

    <!-- Comment / Reason 弹窗 -->
    <div v-if="showCommentDialog" class="dialog-overlay" @click.self="showCommentDialog = false">
      <div class="dialog-box">
        <h4>{{ dialogTitle }}</h4>
        <textarea v-model="dialogComment" :placeholder="dialogPlaceholder" rows="3" class="comment-input" />
        <div v-if="isRejectAction && !dialogComment" class="validation-error">必须填写原因</div>
        <div v-if="isExcludeAction && !dialogComment" class="validation-error">必须填写排除原因</div>
        <div class="dialog-actions">
          <button class="action-btn secondary" @click="showCommentDialog = false">取消</button>
          <button class="action-btn primary" :disabled="actionRequiresReason && !dialogComment" @click="confirmDialog">确认</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'DecisionMemoryReviewCard',
  props: {
    view: { type: Object, default: null },
    detailId: { type: Number, default: null },
  },
  data() {
    return {
      showCommentDialog: false,
      dialogComment: '',
      dialogExclusionReason: '',
      pendingAction: null,
    };
  },
  computed: {
    statusClass() {
      const map = { 'DIRECT_TO_DRAFT': 'success', 'HINT_ONLY': 'warning', 'FULL_ANALYSIS': 'info', 'BLOCK_DUPLICATE': 'error' };
      return map[this.view.reuseMode] || 'info';
    },
    statusBadgeText() {
      const map = { 'DIRECT_TO_DRAFT': '已命中历史', 'HINT_ONLY': '相似历史', 'FULL_ANALYSIS': '未复用', 'BLOCK_DUPLICATE': '重复' };
      return map[this.view && this.view.reuseMode] || '未分析';
    },
    dialogTitle() {
      if (!this.pendingAction) return '';
      const map = { 'REJECT_MEMORY_SUGGESTION': '拒绝历史建议', 'MARK_ONE_OFF': '标记为一次性处理', 'MARK_EXCLUDED_FROM_MEMORY': '标记不进入记忆' };
      return map[this.pendingAction.actionCode] || '确认操作';
    },
    dialogPlaceholder() {
      const map = { 'REJECT_MEMORY_SUGGESTION': '请说明拒绝原因（必填）', 'MARK_ONE_OFF': '请说明为什么是一次性处理', 'MARK_EXCLUDED_FROM_MEMORY': '请填写排除原因（必填）' };
      return map[this.pendingAction && this.pendingAction.actionCode] || '请填写说明';
    },
    actionRequiresReason() {
      return this.pendingAction && this.pendingAction.actionCode === 'REJECT_MEMORY_SUGGESTION'
        || this.pendingAction && this.pendingAction.actionCode === 'MARK_EXCLUDED_FROM_MEMORY';
    },
    isRejectAction() {
      return this.pendingAction && this.pendingAction.actionCode === 'REJECT_MEMORY_SUGGESTION';
    },
    isExcludeAction() {
      return this.pendingAction && this.pendingAction.actionCode === 'MARK_EXCLUDED_FROM_MEMORY';
    },
  },
  methods: {
    formatConfidence(v) {
      if (v === null || v === undefined) return '-';
      const n = typeof v === 'string' ? parseFloat(v) : v;
      return (n * 100).toFixed(0) + '%';
    },
    hintLabel(code) {
      const map = {
        'MEMORY_DIRECT_REUSE_REQUIRES_REVIEW': '系统建议：已复用历史分录，需人工确认',
        'MEMORY_HINT_ONLY': '系统提示：历史建议仅供参考',
        'MEMORY_FULL_ANALYSIS_REQUIRED': '系统提示：进入完整分析流程',
        'MEMORY_DUPLICATE_BLOCKED': '系统阻断：疑似重复处理',
        'MEMORY_FACT_PREDICATE_CHANGED': '事实谓词变化',
        'MEMORY_COUNTERPARTY_ROLE_CHANGED': '对手方角色变化',
        'MEMORY_CASH_SUBJECT_UNRESOLVED': '现金科目未解析',
        'MEMORY_FX_PAIR_MISSING': '换汇缺少配对流水',
      };
      return map[code] || code;
    },
    btnClass(actionCode) {
      const map = {
        'ACCEPT_MEMORY_SUGGESTION': 'primary',
        'MODIFY_MEMORY_SUGGESTION': 'secondary',
        'REJECT_MEMORY_SUGGESTION': 'danger',
        'MARK_ONE_OFF': 'secondary',
        'MARK_ELIGIBLE_FOR_MEMORY': 'primary',
        'MARK_EXCLUDED_FROM_MEMORY': 'danger',
        'CONFIRM_AS_DRAFT': 'primary',
      };
      return map[actionCode] || 'secondary';
    },
    handleActionClick(act) {
      if (act.actionCode === 'ACCEPT_MEMORY_SUGGESTION') {
        this.submitAction(act.actionCode, '', null);
        return;
      }
      if (act.actionCode === 'MODIFY_MEMORY_SUGGESTION') {
        this.$emit('modify', { action: act, view: this.view, detailId: this.detailId });
        return;
      }
      this.pendingAction = act;
      this.dialogComment = '';
      this.showCommentDialog = true;
    },
    confirmDialog() {
      if (this.actionRequiresReason && !this.dialogComment) return;
      const exclusionReason = this.isExcludeAction ? this.dialogComment : null;
      this.submitAction(this.pendingAction.actionCode, this.dialogComment, exclusionReason);
      this.showCommentDialog = false;
      this.pendingAction = null;
      this.dialogComment = '';
    },
    async submitAction(actionCode, comment, exclusionReason) {
      try {
        const payload = {
          actionCode,
          reuseMode: this.view.reuseMode,
          reviewerName: '审核员',
          reviewComment: comment || '',
          traceId: this.view.currentEventSnapshot && this.view.currentEventSnapshot.traceId || null,
        };
        if (exclusionReason) {
          payload.memoryExclusionReason = exclusionReason;
        }
        await this.$api.businessVoucher.submitMemoryReviewAction(this.detailId, payload);
        this.$emit('refreshed');
      } catch (e) {
        this.$emit('error', e && e.msg || '提交审核操作失败');
      }
    },
  },
};
</script>

<style scoped>
.decision-memory-review-card { border: 1px solid #e0e0e0; border-radius: 8px; margin: 12px 0; overflow: hidden; font-size: 13px; }
.memory-header { padding: 12px 16px; }
.memory-header.success { background: #f0fff4; border-bottom: 2px solid #52c41a; }
.memory-header.warning { background: #fffbe6; border-bottom: 2px solid #faad14; }
.memory-header.info { background: #e6f7ff; border-bottom: 2px solid #1890ff; }
.memory-header.error { background: #fff2f0; border-bottom: 2px solid #ff4d4f; }
.memory-status-row { display: flex; align-items: center; gap: 8px; margin-bottom: 6px; }
.memory-badge { display: inline-block; padding: 2px 8px; border-radius: 4px; font-size: 12px; font-weight: 600; color: #fff; }
.memory-badge.success { background: #52c41a; }
.memory-badge.warning { background: #faad14; }
.memory-badge.info { background: #1890ff; }
.memory-badge.error { background: #ff4d4f; }
.memory-mode-label { font-weight: 600; font-size: 14px; }
.memory-summary { color: #666; margin: 4px 0; line-height: 1.5; }
.memory-need-review-tag { display: inline-block; background: #fff7e6; border: 1px solid #ffd591; color: #d46b08; padding: 2px 8px; border-radius: 4px; font-size: 12px; margin-top: 4px; }
.memory-meta { display: flex; flex-wrap: wrap; gap: 6px; padding: 8px 16px; background: #fafafa; border-bottom: 1px solid #f0f0f0; }
.meta-chip { display: inline-block; padding: 2px 8px; border-radius: 4px; font-size: 12px; background: #e6f7ff; color: #1890ff; border: 1px solid #91d5ff; }
.meta-chip.exact { background: #f6ffed; color: #52c41a; border-color: #b7eb8f; }
.meta-chip.strong { background: #fffbe6; color: #d48806; border-color: #ffe58f; }
.meta-chip.weak { background: #fff7e6; color: #d46b08; border-color: #ffd591; }
.meta-chip.warn { background: #fff2f0; color: #ff4d4f; border-color: #ffccc7; }
.memory-section { padding: 8px 16px; border-bottom: 1px solid #f5f5f5; }
.memory-section details { cursor: pointer; }
.memory-section summary { font-weight: 600; color: #333; padding: 4px 0; }
.audit-explanation { background: #fafafa; border-left: 3px solid #1890ff; padding: 8px 12px; border-radius: 4px; font-size: 12px; color: #555; line-height: 1.6; }
.hint-item { padding: 4px 8px; background: #fffbe6; border-radius: 4px; margin-bottom: 4px; font-size: 12px; color: #d48806; }
.risk-tag { display: inline-block; padding: 2px 8px; border-radius: 4px; font-size: 12px; background: #fff2f0; color: #ff4d4f; border: 1px solid #ffccc7; margin: 2px; }
.reason-list, .signal-list { margin: 4px 0; padding-left: 20px; font-size: 12px; color: #666; }
.reason-list li, .signal-list li { margin-bottom: 2px; }
.signal-group { margin-bottom: 8px; }
.signal-group-label { font-weight: 600; font-size: 12px; color: #333; margin: 4px 0; }
.snapshot-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 4px 16px; padding: 8px 0; }
.snapshot-field { display: flex; font-size: 12px; }
.snapshot-field label { color: #999; min-width: 70px; }
.snapshot-field span { color: #333; }
.mono { font-family: 'SF Mono', 'Menlo', monospace; font-size: 11px; }
.pattern-table { width: 100%; border-collapse: collapse; font-size: 12px; margin-top: 4px; }
.pattern-table th { background: #fafafa; padding: 6px 8px; text-align: left; border-bottom: 1px solid #f0f0f0; }
.pattern-table td { padding: 6px 8px; border-bottom: 1px solid #f5f5f5; }
.hint-card { padding: 8px; margin: 4px 0; background: #fafafa; border-radius: 4px; font-size: 12px; }
.hint-row { display: flex; gap: 4px; margin-bottom: 2px; }
.hint-row label { color: #999; min-width: 40px; }
.memory-actions { display: flex; flex-wrap: wrap; gap: 6px; padding: 12px 16px; }
.action-btn { padding: 6px 14px; border: 1px solid #d9d9d9; border-radius: 4px; cursor: pointer; font-size: 12px; transition: all .2s; }
.action-btn.primary { background: #1890ff; color: #fff; border-color: #1890ff; }
.action-btn.secondary { background: #fff; color: #333; }
.action-btn.danger { background: #ff4d4f; color: #fff; border-color: #ff4d4f; }
.action-btn.primary:hover { background: #40a9ff; }
.action-btn.danger:hover { background: #ff7875; }
.action-btn:disabled { opacity: .5; cursor: not-allowed; }
.dialog-overlay { position: fixed; inset: 0; background: rgba(0,0,0,.4); display: flex; align-items: center; justify-content: center; z-index: 1000; }
.dialog-box { background: #fff; border-radius: 8px; padding: 20px; min-width: 360px; max-width: 500px; }
.dialog-box h4 { margin: 0 0 12px; }
.comment-input { width: 100%; border: 1px solid #d9d9d9; border-radius: 4px; padding: 8px; font-size: 13px; resize: vertical; }
.validation-error { color: #ff4d4f; font-size: 12px; margin-top: 4px; }
.dialog-actions { display: flex; gap: 8px; justify-content: flex-end; margin-top: 12px; }
</style>
